import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ACADEMY_LOGO, ACADEMY_LOGO_H, IMAGES } from "../data/assets";
import { BOOK, CHAPTERS } from "../data/chapters";
import { useProgress } from "../context/ProgressContext";
import { useSettings } from "../context/SettingsContext";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import AuthDialog from "../components/AuthDialog";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { youtubeEmbed } from "../lib/video";
import { ArrowRight, Play, BookOpen, CalendarDays, Star, Download, Lock, LogIn, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const stagger = { animate: { transition: { staggerChildren: 0.09 } } };
const rise = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

export default function Cover() {
  const navigate = useNavigate();
  const { completed } = useProgress();
  const { bookingLink, featured } = useSettings();
  const { hasAccess, player, buy } = usePlayerAuth();
  const coachPreview = !!localStorage.getItem("coachToken");
  const canRead = hasAccess || coachPreview;
  const { canInstall, install } = usePwaInstall();
  const [showAuth, setShowAuth] = useState(false);
  const [buying, setBuying] = useState(false);
  const featuredEmbed = youtubeEmbed(featured?.video);
  const [seenFeatured, setSeenFeatured] = useState(() => localStorage.getItem("noSoccerSeenFeatured") || "");
  const isNewFeatured = !!featured?.video && seenFeatured !== featured.video;
  const markFeaturedSeen = () => {
    if (featured?.video) {
      localStorage.setItem("noSoccerSeenFeatured", featured.video);
      setSeenFeatured(featured.video);
    }
  };
  const startBuy = async () => {
    setBuying(true);
    try { await buy(); }
    catch (err) {
      setBuying(false);
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again in a moment.");
    }
  };
  const openChapter = (id) => { if (canRead) navigate(`/read/${id}`); else navigate("/login"); };
  const start = () => openChapter(CHAPTERS[0].id);

  return (
    <div className="min-h-screen bg-[#111111] text-white overflow-x-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center grain">
        <div className="absolute inset-0">
          <img loading="lazy" decoding="async" src={IMAGES.hero} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/90 to-[#111111]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-[#111111]/60" />
        </div>

        <motion.div variants={stagger} initial="initial" animate="animate" className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 w-full">
          <motion.div variants={rise} className="flex items-center gap-4 mb-8">
            <img src={ACADEMY_LOGO_H} alt="The Next O Technical Academy" className="h-14 sm:h-20 w-auto object-contain drop-shadow-[0_4px_12px_rgba(196,30,36,0.4)]" />
            <div className="hidden sm:block">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#C41E24] font-bold mt-1">Creating Opportunities With Elite Training</div>
            </div>
          </motion.div>

          <motion.div variants={rise} className="inline-block bg-[#C41E24] px-3 py-1 mb-5">
            <span className="font-subheadings text-xs font-bold uppercase tracking-[0.25em]">{BOOK.blueprint}</span>
          </motion.div>

          <motion.h1 variants={rise} className="font-cover text-6xl sm:text-8xl md:text-9xl uppercase leading-[0.85] tracking-tight max-w-4xl">
            The Next O <span className="text-[#C41E24]">Technical</span> Academy
          </motion.h1>

          <motion.p variants={rise} className="font-subheadings text-sm sm:text-base font-bold uppercase tracking-[0.2em] text-white/55 mt-4">
            {BOOK.title}
          </motion.p>

          <motion.p variants={rise} className="font-subheadings text-lg sm:text-2xl font-semibold text-white/80 mt-4 max-w-xl">
            {BOOK.tagline}
          </motion.p>

          <motion.div variants={rise} className="flex flex-wrap items-center gap-4 mt-10">
            {hasAccess ? (
              <>
                <button data-testid="start-reading-btn" onClick={start} className="group flex items-center gap-3 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider py-4 px-8 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
                  {completed.length > 0 ? "Continue Training" : "Start Training"}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button data-testid="hub-nav-btn" onClick={() => navigate("/")} className="flex items-center gap-3 bg-transparent border border-white/25 hover:border-white text-white font-subheadings font-bold uppercase tracking-wider py-4 px-8 rounded-sm transition-colors">
                  My Hub
                </button>
              </>
            ) : (
              <>
                <button data-testid="buy-ebook-btn" onClick={startBuy} disabled={buying} className="group flex items-center gap-3 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider py-4 px-8 rounded-sm transition-colors disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
                  <ShoppingCart size={18} /> {buying ? "Redirecting…" : "Enroll in the Blueprint — $49"}
                </button>
                <button data-testid="login-btn" onClick={() => navigate("/login")} className="flex items-center gap-3 bg-transparent border border-white/25 hover:border-white text-white font-subheadings font-bold uppercase tracking-wider py-4 px-8 rounded-sm transition-colors">
                  <LogIn size={18} className="text-[#C41E24]" /> Log In
                </button>
                {coachPreview && (
                  <button data-testid="coach-preview-btn" onClick={start} className="flex items-center gap-3 bg-white/5 border border-[#C41E24]/50 hover:border-[#C41E24] text-white font-subheadings font-bold uppercase tracking-wider py-4 px-8 rounded-sm transition-colors">
                    <BookOpen size={18} className="text-[#C41E24]" /> Coach Preview
                  </button>
                )}
              </>
            )}
            <a data-testid="book-session-btn" href={bookingLink} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 text-white/70 hover:text-white font-subheadings font-bold uppercase tracking-wider py-4 text-sm transition-colors">
              <CalendarDays size={18} className="text-[#C41E24]" /> Book a Session
            </a>
            {canInstall && (
              <button data-testid="install-app-btn" onClick={install} className="group flex items-center gap-2 text-white/70 hover:text-white font-subheadings font-bold uppercase tracking-wider py-4 text-sm transition-colors">
                <Download size={18} className="text-[#C41E24]" /> Install App
              </button>
            )}
          </motion.div>
          <motion.div variants={rise} className="flex items-center gap-2 text-white/60 text-sm mt-5">
            <BookOpen size={16} /> {CHAPTERS.length} chapters · 30+ moves &amp; combos
          </motion.div>

          <motion.div variants={rise} className="mt-14 flex items-center gap-3 text-sm text-white/50">
            <div className="w-10 h-px bg-[#C41E24]" />
            By <span className="text-white font-semibold">{BOOK.author}</span> · {BOOK.authorRole}
          </motion.div>
        </motion.div>
      </section>

      {/* FEATURED VIDEO OF THE WEEK */}
      {featured?.video && (
        <section className="relative py-20 px-6 sm:px-10 max-w-6xl mx-auto" data-testid="featured-video-section">
          <div className="absolute inset-0 red-glow pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 bg-[#C41E24] px-3 py-1 mb-5">
                {isNewFeatured ? (
                  <span className="relative flex h-2.5 w-2.5" data-testid="featured-new-badge">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                  </span>
                ) : (
                  <Star size={14} className="text-white" />
                )}
                <span className="font-subheadings text-xs font-bold uppercase tracking-[0.25em]">
                  {isNewFeatured ? "New This Week" : "Featured Video Of The Week"}
                </span>
              </div>
              <h2 className="font-headings text-4xl sm:text-5xl uppercase tracking-wide leading-none mb-4">
                {featured.title || "This Week's Focus"}
              </h2>
              {featured.note && <p className="text-base md:text-lg text-white/65 leading-relaxed max-w-lg">{featured.note}</p>}
              <div className="mt-6 flex items-center gap-3 text-sm text-white/40">
                <div className="w-10 h-px bg-[#C41E24]" /> Handpicked by Coach {BOOK.author}
              </div>
            </motion.div>

            <motion.div onClick={markFeaturedSeen} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              className="rounded-xl overflow-hidden border border-white/10 aspect-video shadow-[0_16px_48px_rgba(0,0,0,0.6)]">
              {featuredEmbed ? (
                <iframe className="w-full h-full" src={featuredEmbed} title="Featured video of the week" frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="featured-video-iframe" />
              ) : (
                <a href={featured.video} target="_blank" rel="noopener noreferrer" data-testid="featured-video-link"
                  className="w-full h-full flex flex-col items-center justify-center bg-[#141414] hover:bg-[#1a1a1a] transition-colors gap-3">
                  <Play size={40} className="text-[#C41E24]" />
                  <span className="font-subheadings text-sm font-bold uppercase tracking-widest">Watch This Week's Video</span>
                </a>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* TABLE OF CONTENTS */}
      <section className="relative py-24 px-6 sm:px-10 max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-[#C41E24] font-bold uppercase tracking-[0.3em] text-xs mb-3">The Curriculum</div>
            <h2 className="font-headings text-4xl sm:text-5xl uppercase tracking-wide">The Complete Curriculum</h2>
          </div>
          <button onClick={start} data-testid="toc-start-btn" className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white/70 hover:text-[#C41E24] transition-colors">
            <Play size={14} /> Begin Chapter 1
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CHAPTERS.map((c, i) => (
            <motion.button
              key={c.id}
              data-testid={`toc-card-${c.id}`}
              onClick={() => openChapter(c.id)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 2) * 0.05 }}
              whileHover={{ x: 6 }}
              className="group flex items-center gap-5 text-left bg-[#141414] border border-white/10 hover:border-[#C41E24]/50 rounded-lg p-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
            >
              <span className="font-cover text-4xl text-white/15 group-hover:text-[#C41E24] transition-colors leading-none">{String(i + 1).padStart(2, "0")}</span>
              <div className="flex-1">
                <h3 className="font-headings text-xl uppercase tracking-wide text-white leading-none mb-1">{c.title}</h3>
                <p className="text-xs text-white/45 uppercase tracking-wider font-semibold">{c.kicker} · {c.minutes} min</p>
              </div>
              {canRead
                ? <ArrowRight size={18} className="text-white/20 group-hover:text-[#C41E24] group-hover:translate-x-1 transition-all" />
                : <Lock size={16} className="text-white/25 group-hover:text-[#C41E24] transition-colors" />}
            </motion.button>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 px-6 text-center">
        <img src={ACADEMY_LOGO} alt="The Next O Technical Academy" className="w-36 h-auto object-contain mx-auto mb-3" />
        <p className="text-xs text-white/40 uppercase tracking-widest">© 2026 Next O Soccer · Elite Technical Training System · Ball Mastery Vol. 1</p>
        <p className="text-[11px] text-white/30 mt-2 max-w-md mx-auto">Players should train safely and consult a coach when appropriate.</p>
      </footer>

      <AuthDialog open={showAuth} onClose={() => setShowAuth(false)} initialMode="login" onSuccess={() => navigate("/")} />
    </div>
  );
}
